import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, authApi } from '../../lib/api';
import { useSocketStore } from '../../store/socketStore';
import { useAuthStore } from '../../store/authStore';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { 
  Users, Activity, FileText, TrendingUp, 
  RefreshCw, Clock, Crown, AlertTriangle, X
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export function DashboardPage() {
  const navigate = useNavigate();
  const { subscribe } = useSocketStore();
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [showTrialBanner, setShowTrialBanner] = useState(true);

  // Redirect SuperAdmin to Admin Panel
  useEffect(() => {
    if (user?.role === 'superadmin') {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, tenantData] = await Promise.all([
        dashboardApi.stats(),
        authApi.tenantInfo()
      ]);
      setStats(statsData);
      setTenantInfo(tenantData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't fetch data if user is superadmin (will be redirected)
    if (user?.role === 'superadmin') return;
    
    fetchData();

    // Subscribe to real-time updates
    const unsubMatch = subscribe('MATCH_CREATED', () => {
      fetchData();
      toast.info('New match created');
    });
    const unsubTx = subscribe('TRANSACTION_CREATED', () => {
      fetchData();
    });

    return () => {
      unsubMatch();
      unsubTx();
    };
  }, [subscribe, user]);

  const statCards = [
    { 
      label: 'Open Matches', 
      value: stats?.open_matches || 0, 
      icon: Activity, 
      color: 'text-blue-500',
      onClick: () => navigate('/master', { state: { tab: 'matches' } })
    },
    { 
      label: 'Open Sessions', 
      value: stats?.open_sessions || 0, 
      icon: FileText, 
      color: 'text-emerald-500',
      onClick: () => navigate('/master', { state: { tab: 'sessions' } })
    }
  ];

  // Show loading while redirecting superadmin
  if (user?.role === 'superadmin') {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Redirecting to Admin Panel...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Trial Banner */}
      {tenantInfo?.is_trial && showTrialBanner && (
        <div 
          className={cn(
            "rounded-lg p-4 flex items-center justify-between",
            tenantInfo.trial_days_remaining <= 7 
              ? "bg-amber-500/10 border border-amber-500/30" 
              : "bg-primary/10 border border-primary/30"
          )}
          data-testid="trial-banner"
        >
          <div className="flex items-center gap-3">
            {tenantInfo.trial_days_remaining <= 7 ? (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            ) : (
              <Clock className="h-5 w-5 text-primary" />
            )}
            <div>
              <p className={cn(
                "font-medium",
                tenantInfo.trial_days_remaining <= 7 ? "text-amber-500" : "text-primary"
              )}>
                {tenantInfo.trial_days_remaining <= 0 
                  ? "Your free trial has expired" 
                  : `${tenantInfo.trial_days_remaining} days left in your free trial`}
              </p>
              <p className="text-sm text-muted-foreground">
                {tenantInfo.trial_days_remaining <= 0 
                  ? "Upgrade now to continue using all features"
                  : "Upgrade to unlock unlimited access and premium features"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/select-plan')}
              className={cn(
                tenantInfo.trial_days_remaining <= 7 
                  ? "bg-amber-500 hover:bg-amber-600" 
                  : ""
              )}
              data-testid="upgrade-btn"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade Now
            </Button>
            <button
              onClick={() => setShowTrialBanner(false)}
              className="p-1 rounded hover:bg-gray-700/50 transition-colors"
              title="Dismiss"
              data-testid="close-trial-banner"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">Dashboard</h1>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-dashboard">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        {statCards.map((stat, idx) => (
          <div 
            key={idx}
            onClick={stat.onClick}
            className="group-box cursor-pointer hover:border-primary/50 transition-colors"
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
                <p className="font-mono text-2xl font-bold mt-1">{stat.value}</p>
              </div>
              <stat.icon className={`h-8 w-8 ${stat.color} opacity-50`} />
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <GroupBox label="Quick Actions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Button 
            variant="outline" 
            className="h-auto py-3 flex-col gap-1"
            onClick={() => navigate('/transactions')}
            data-testid="quick-action-match-txn"
          >
            <Activity className="h-5 w-5" />
            <span className="text-xs">Match Transaction</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-3 flex-col gap-1"
            onClick={() => navigate('/compact')}
            data-testid="quick-action-compact"
          >
            <FileText className="h-5 w-5" />
            <span className="text-xs">Compact Entry (F6)</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-3 flex-col gap-1"
            onClick={() => navigate('/trial-balance')}
            data-testid="quick-action-trial-balance"
          >
            <TrendingUp className="h-5 w-5" />
            <span className="text-xs">Trial Balance (F8)</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-3 flex-col gap-1"
            onClick={() => navigate('/settlement')}
            data-testid="quick-action-settlement"
          >
            <Users className="h-5 w-5" />
            <span className="text-xs">Settlement</span>
          </Button>
        </div>
      </GroupBox>
    </div>
  );
}
